import {
  ChangeEvent,
  FC,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";

import useThemeColor from "../useThemeColor.hook";
import Typography from "../Typography/Typography";
import Box from "../Box/Box";
import { Variant } from "../Typography/typography.type";
import useControlled from "../useControlled";
import useMutationObserver from "../useMutationObserver";

const ToggleButton = styled.button`
  all: inherit;
  border: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0.5em 0;
  cursor: pointer;
  &[disabled] {
    opacity: 0.3;
  }
`;

const CollapsibleContent = styled.div<{ css?: string; duration: number }>`
  overflow: hidden;
  ${({ duration }) =>
    `transition: max-height ${duration / 1000}s, opacity ${
      (duration * 2) / 1000
    }s;`}

  ${(props) => props.css}
`;

const getHeight = (element: HTMLElement) => {
  return element.scrollHeight;
};

export interface CollapsibleProps {
  title: ReactNode;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * The current state of closed/unclosed
   */
  closed?: boolean;
  /**
   * The state by default when the component is uncontrolled.
   * @default true
   */
  defaultClosed?: boolean;
  /**
   * Callback fired when the state is changed.
   *
   * @param {object} event The event source of the callback.
   * @param {number} page The closed state.
   */
  onChange?: (event: ChangeEvent<HTMLElement>, isClosed: boolean) => void;
  color?: string;
  /**
   * Typography variant to render the title as
   */
  as?: Variant;
  /**
   * duration in miliseconds
   */
  animationDuration?: number;
  disabled?: boolean;
}

const Collapsible: FC<CollapsibleProps> = ({
  id,
  title,
  children,
  closed,
  defaultClosed = true,
  onChange,
  color = "text-color-hover",
  as = "h6",
  animationDuration = 400,
  disabled = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useControlled({
    controlled: closed,
    default: defaultClosed,
    name: "collapsible",
    state: "page",
  });

  const [hideContent, setHideContent] = useState<boolean>(isCollapsed);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [opacity, setOpacity] = useState<number>(isCollapsed ? 0 : 1);
  const [overflow, setOverflow] = useState<string>(
    isCollapsed ? "hidden" : "visible"
  );
  const ref = useRef<HTMLDivElement>(null);

  const [mutationDetected, setMutationDetected] = useMutationObserver(
    ref,
    "childList"
  );

  useEffect(() => {
    const element = ref.current;

    if (element && (maxHeight > 0 || mutationDetected)) {
      const newHeight = getHeight(element) || 0;
      setMaxHeight(newHeight);
      setMutationDetected(false);
    }
  }, [children, ref, maxHeight, mutationDetected, setMutationDetected]);

  useEffect(() => {
    let timeout = 0;
    let secondTimeout = 0;
    const boxHeight = ref && ref.current !== null ? getHeight(ref.current) : 0;

    if (!isCollapsed) {
      setHideContent(false);
      // to make sure setHideContent is called before all other set states
      timeout = window.setTimeout(() => {
        setMaxHeight(boxHeight);
        setOpacity(1);
        secondTimeout = window.setTimeout(() => {
          setOverflow("visible");
        }, animationDuration);
      }, 0);
      return;
    }

    setOpacity(0);
    timeout = window.setTimeout(() => {
      setMaxHeight(0);
      setOverflow("hidden");
      secondTimeout = window.setTimeout(() => {
        setHideContent(true);
      }, animationDuration);
    }, animationDuration);

    return () => {
      clearTimeout(timeout);
      clearTimeout(secondTimeout);
    };
  }, [hideContent, isCollapsed, animationDuration]);

  const hexColor = useThemeColor(color);

  const toggle = (event: MouseEvent<HTMLElement>) => {
    // ignoring typescript because we want to check if closed is not passed in, eq null or undefined
    // if(!closed) when closed is false will be true but we don't want to execute in that case
    // @ts-ignore
    if (closed !== true || closed !== false) {
      setIsCollapsed((prev: boolean) => !prev);
    }
    if (onChange) {
      onChange(event as unknown as ChangeEvent<HTMLElement>, !isCollapsed);
    }
  };

  return (
    <Box id={id}>
      <Typography variant={as} margin={0}>
        <ToggleButton
          disabled={disabled}
          aria-expanded={isCollapsed ? "false" : "true"}
          onClick={toggle}
        >
          {title}
          {isCollapsed && (
            <FontAwesomeIcon
              size="lg"
              color={hexColor}
              icon={faAngleDown}
            ></FontAwesomeIcon>
          )}
          {!isCollapsed && (
            <FontAwesomeIcon
              size="lg"
              color={hexColor}
              icon={faAngleUp}
            ></FontAwesomeIcon>
          )}
        </ToggleButton>
      </Typography>
      <CollapsibleContent
        hidden={hideContent}
        ref={ref}
        duration={animationDuration}
        css={`
          max-height: ${maxHeight}px;
          opacity: ${opacity};
          overflow: ${overflow};
        `}
      >
        {children}
      </CollapsibleContent>
    </Box>
  );
};

export default Collapsible;
